import {
	ErrorComponent,
	type ErrorComponentProps,
	useRouter,
	useRouterState,
} from "@tanstack/react-router";
import { Button, Typography } from "@mui/material";
import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import { t } from "i18next";
import { NotFoundErrorPage } from "./fallbacks/not-found-error-page/not-found-error-page";
import { UnknownErrorPage } from "./fallbacks/unknown-error-page/unknown-error-page";
import { Flex } from "@/components/layout/flex/flex";

export function RouteErrorComponent({ error, reset }: ErrorComponentProps) {
	const router = useRouter();
	const pathname = useRouterState({ select: (state) => state.location.pathname });

	// Report error to Sentry when component mounts
	useEffect(() => {
		// Don't report 404 errors to Sentry
		if (
			!error.message.includes("404") &&
			!error.message.includes("Not Found")
		) {
			Sentry.captureException(error, {
				tags: {
					errorBoundary: "route",
				},
				contexts: {
					routing: {
						location: window.location.pathname,
						href: window.location.href,
					},
				},
			});
		}
	}, [error]);

	// Check if it's a 404 error
	if (error.message.includes("404") || error.message.includes("Not Found")) {
		return <NotFoundErrorPage />;
	}

	function handleGoHome() {
		void router.navigate({ to: "/" });
	}

	// A crash mid-signup used to leave "Back to Home" as the only way out, and
	// `/` sends a user with no platform role to /unauthorized — dropping them
	// out of onboarding entirely. Send them back into the flow instead;
	// /onboarding re-resolves the right step from server state.
	const isOnboarding = pathname.startsWith("/onboarding");

	function handleGoBackToOnboarding() {
		void router.navigate({ to: "/onboarding" });
	}

	// Retrying re-runs the failed render without a full page reload, which
	// keeps any in-memory state the user still has.
	function handleRetry() {
		void router.invalidate();
		reset();
	}

	// Custom actions for route errors
	const actions = (
		<Flex mt={2} gap={2} justifyContent="center">
			<Button
				onClick={isOnboarding ? handleGoBackToOnboarding : handleGoHome}
			>
				{isOnboarding ? t("Back to sign-up") : t("Back to Home")}
			</Button>
			<Button onClick={handleRetry} variant="contained" color="primary">
				{t("Try again")}
			</Button>
		</Flex>
	);

	// For development, also show the default error component for debugging
	const isDevelopment = import.meta.env.DEV;

	return (
		<>
			<UnknownErrorPage actions={actions} />
			{isDevelopment && (
				<Flex mt={4} p={2} bgcolor="background.paper" borderRadius={1}>
					<Typography
						variant="caption"
						component="pre"
						sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}
					>
						{error.stack || error.message}
					</Typography>
				</Flex>
			)}
		</>
	);
}

// Export a fallback to the default ErrorComponent for unhandled cases
export function RouteErrorFallback({ error, reset }: ErrorComponentProps) {
	// Check if we should use our custom component
	if (error instanceof Error) {
		return <RouteErrorComponent error={error} reset={reset} />;
	}

	// Fallback to TanStack's default error component
	return <ErrorComponent error={error} />;
}
